import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2 } from "lucide-react";

import LeadGate from "./LeadGate";
import CoverPage from "./CoverPage";
import WelcomePage from "./WelcomePage";
import CityPage from "./CityPage";
import MatchmakerPage from "./MatchmakerPage";
import EssentialsPage from "./EssentialsPage";
import AboutMePage from "./AboutMePage";
import BackCoverPage from "./BackCoverPage";
import TableOfContents from "./TableOfContents";
import { ReaderHeader, ReaderFooterNav } from "./ReaderControls";
import {
    useLeadGate,
    useBookData,
    useBookPages,
    usePageNavigation,
    usePdfDownload,
} from "../hooks/useBookReader";

export default function BookReader() {
    const { reader, unlocked, setReader, setUnlocked } = useLeadGate();
    const book = useBookData();
    const pages = useBookPages(book);
    const total = pages.length;
    const { pageIndex, goNext, goPrev, goTo } = usePageNavigation(total);
    const { downloading, download } = usePdfDownload(reader?.name);
    const [tocOpen, setTocOpen] = useState(false);

    const current = pages[pageIndex];

    // Keyboard navigation
    useEffect(() => {
        if (!unlocked) return undefined;
        const onKey = (e) => {
            if (e.key === "ArrowRight") goNext();
            if (e.key === "ArrowLeft") goPrev();
            if (e.key === "Escape") setTocOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [unlocked, goNext, goPrev]);

    if (!unlocked) {
        return (
            <LeadGate
                onUnlock={(form) => {
                    setReader(form);
                    setUnlocked(true);
                }}
            />
        );
    }

    if (!book || !current) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-cream">
                <Loader2 className="animate-spin text-navy" size={32} />
            </div>
        );
    }

    const showChrome = current.id !== "cover";
    const handleTocNavigate = (i) => {
        goTo(i);
        setTocOpen(false);
    };

    return (
        <div className="relative min-h-screen bg-cream">
            {showChrome && (
                <ReaderHeader
                    label={current.label}
                    progressPercent={((pageIndex + 1) / total) * 100}
                    downloading={downloading}
                    onOpenToc={() => setTocOpen(true)}
                    onDownload={download}
                />
            )}

            <AnimatePresence mode="wait">
                <motion.main
                    key={current.id}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.45, ease: "easeOut" }}
                    data-testid={`page-${current.id}`}
                >
                    <PageRenderer
                        page={current}
                        pageNum={pageIndex + 1}
                        totalPages={total}
                        book={book}
                        readerName={reader?.name}
                        onStart={goNext}
                        onDownload={download}
                    />
                </motion.main>
            </AnimatePresence>

            {showChrome && (
                <ReaderFooterNav
                    pageNum={pageIndex + 1}
                    totalPages={total}
                    onPrev={goPrev}
                    onNext={goNext}
                />
            )}

            <TableOfContents
                open={tocOpen}
                pages={pages}
                currentIndex={pageIndex}
                onClose={() => setTocOpen(false)}
                onNavigate={handleTocNavigate}
            />
        </div>
    );
}

function PageRenderer({ page, pageNum, totalPages, book, readerName, onStart, onDownload }) {
    if (page.id === "cover") {
        return <CoverPage readerName={readerName} onStart={onStart} />;
    }
    if (page.id === "welcome") {
        return (
            <WelcomePage
                welcome={book.welcome}
                comparison={book.comparison}
                pageNum={pageNum}
                totalPages={totalPages}
            />
        );
    }
    if (page.city) {
        return <CityPage city={page.city} pageNum={pageNum} totalPages={totalPages} />;
    }
    if (page.id === "matchmaker") {
        return (
            <MatchmakerPage
                matchmaker={book.matchmaker}
                pageNum={pageNum}
                totalPages={totalPages}
            />
        );
    }
    if (page.id === "essentials") {
        return (
            <EssentialsPage
                essentials={book.essentials}
                pageNum={pageNum}
                totalPages={totalPages}
            />
        );
    }
    if (page.id === "about") {
        return (
            <AboutMePage
                pageNum={pageNum}
                totalPages={totalPages}
                onDownload={onDownload}
            />
        );
    }
    if (page.id === "back") {
        return <BackCoverPage pageNum={pageNum} totalPages={totalPages} />;
    }
    return null;
}
